// noinspection DuplicatedCode
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { Colors } from "../../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useMutation } from "@tanstack/react-query";
import { fetchGet, fetchPost } from "../../../fetchers";
import ModalBayar from "../../../components/form/ModalBayar";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import DataTable from "react-data-table-component";
import debounce from "lodash/debounce";
import { BiSearch, BiRefresh, BiFilter } from "react-icons/bi";
import { BsFileEarmarkExcelFill } from "react-icons/bs";

const MonitoringPenerimaanDana = ({}) => {
  const navigate = useNavigate();

  let initialState = {
    Bulan: new Date(),
    BisnisUnitID: "U0002",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations
  );

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [count, setCount] = useState(maxPageGlobal);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasData, setHasData] = useState(false);

  const pageRef = useRef(1);
  const countRef = useRef(maxPageGlobal);
  const searchRef = useRef("");

  // limit WAJIB dikirim. Backend defaultnya limit=10 (cashjournal.controller.go),
  // sedangkan halaman ini memakai paginationServer={false}: paginasi, kotak Cari,
  // dan ekspor Excel semuanya bekerja di atas allData. Tanpa limit, hanya 10 baris
  // teratas (ORDER BY Tanggal DESC) yang pernah sampai ke browser — sisanya tak
  // bisa dicari maupun diekspor, dan tabel tetap melaporkan seolah itu totalnya.
  const mutationData = useMutation((params) =>
    fetchGet(
      `Accounting/Mon/CashJournal/${params.BisnisUnitID}/penerimaandana/${params.Bulan}/${params.Tahun}?page=1&limit=5000`
    )
  );

  // Fungsi untuk melakukan client-side search
  const performClientSideSearch = useCallback((searchTerm, dataToSearch) => {
    if (!searchTerm.trim()) {
      return dataToSearch;
    }

    const lowercasedSearch = searchTerm.toLowerCase();

    return dataToSearch.filter((item) => {
      return (
        (item.CashJournalID &&
          item.CashJournalID.toLowerCase().includes(lowercasedSearch)) ||
        (item.DariKepada &&
          item.DariKepada.toLowerCase().includes(lowercasedSearch)) ||
        (item.JenisTransaksi &&
          item.JenisTransaksi.toLowerCase().includes(lowercasedSearch)) ||
        (item.Keterangan &&
          item.Keterangan.toLowerCase().includes(lowercasedSearch)) ||
        (item.JenisCashJournal &&
          item.JenisCashJournal.toLowerCase().includes(lowercasedSearch)) ||
        (item.Status &&
          item.Status.toLowerCase().includes(lowercasedSearch)) ||
        (item.Tanggal &&
          new Date(item.Tanggal)
            .toLocaleDateString("id-ID")
            .toLowerCase()
            .includes(lowercasedSearch))
      );
    });
  }, []);

  // Debounced search untuk client-side
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        searchRef.current = searchValue;

        if (!allData.length) return;

        const filtered = performClientSideSearch(searchValue, allData);
        setFilteredData(filtered);
        setTotalRows(filtered.length);
        setCurrentPage(1);
        pageRef.current = 1;

        if (searchValue) {
          toast.info(`Menemukan ${filtered.length} data`, {
            position: "top-center",
            autoClose: 1000,
          });
        }
      }, 300),
    [allData, performClientSideSearch]
  );

  // Cleanup debounce
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const bisnisUnitLabel = values.BisnisUnitID === "U0001"
    ? "HO"
    : values.BisnisUnitID === "U0002"
    ? "SO"
    : values.BisnisUnitID;

  const fetchData = useCallback(() => {
    setIsLoading(true);

    const selectedDate = new Date(values.Bulan);
    const bulan = (selectedDate.getMonth() + 1).toString();
    const tahun = selectedDate.getFullYear().toString();

    const selectedUnit = values.BisnisUnitID === "U0001"
      ? "HO"
      : values.BisnisUnitID === "U0002"
      ? "SO"
      : values.BisnisUnitID;

    const apiParams = {
      BisnisUnitID: values.BisnisUnitID,
      Bulan: bulan,
      Tahun: tahun,
    };

    toast.info(`Memuat data Penerimaan Dana ${selectedUnit}...`, {
      position: "top-center",
      autoClose: 1500,
      hideProgressBar: false,
    });

    mutationData.mutate(apiParams, {
      onSuccess: (response) => {
        const data = response?.data || [];
        setAllData(data);
        setFilteredData(data);
        // Pakai "count" dari API, bukan data.length: kalau suatu saat jumlah baris
        // satu periode melewati limit di atas, selisihnya langsung kelihatan.
        setTotalRows(response?.count ?? data.length);
        setHasData(data.length > 0);

        toast.success(`${data.length} data berhasil dimuat!`, {
          position: "top-center",
          autoClose: 1000,
        });
        setIsLoading(false);
        setIsInitialLoad(false);

        setSearchValue("");
        searchRef.current = "";
      },
      onError: (error) => {
        toast.error(`Gagal memuat data: ${error.message}`, {
          position: "top-center",
          autoClose: 2000,
        });
        setIsLoading(false);
        setIsInitialLoad(false);
        setHasData(false);
      },
    });
  }, [mutationData, values.Bulan, values.BisnisUnitID]);

  // Auto-fetch saat halaman pertama dibuka
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modal "Bayar" — sejak rilis 2026-08-14 jurnal tidak lagi terbit saat approval
  // final, melainkan di sini. Halaman penerimaan sempat terlewat saat itu, sehingga
  // dokumen Penerimaan yang sudah disetujui tak punya jalan untuk berjurnal sama
  // sekali dan tak pernah muncul di Buku Bank maupun laporan Jurnal.
  const [bayarModal, setBayarModal] = useState({ show: false, id: "", nominal: "" });
  const [bayarBusy, setBayarBusy] = useState(false);

  const handleSimpanBayar = useCallback(
    async (tanggal) => {
      setBayarBusy(true);
      try {
        // fetchPost memunculkan toast sukses/gagal sendiri dan MELEMPAR error saat
        // gagal — jadi tak perlu toast tambahan di sini.
        await fetchPost("Accounting/CashJournal/bayar", {
          ID: bayarModal.id,
          TanggalBayar: tanggal,
        });
        setBayarModal({ show: false, id: "", nominal: "" });
        fetchData();
      } catch (_) {
        // Modal dibiarkan terbuka saat gagal: penyebab tersering adalah detail
        // lawan kosong / tidak seimbang, dan pengguna perlu konteksnya.
      } finally {
        setBayarBusy(false);
      }
    },
    [bayarModal.id, fetchData]
  );

  // Update filtered data ketika allData berubah
  useEffect(() => {
    if (allData.length > 0 && searchRef.current) {
      const filtered = performClientSideSearch(searchRef.current, allData);
      setFilteredData(filtered);
      setTotalRows(filtered.length);
    }
  }, [allData, performClientSideSearch]);

  // Handle filter dengan button "Buka Data"
  const handleFilter = useCallback(() => {
    pageRef.current = 1;
    setCurrentPage(1);
    searchRef.current = "";
    setSearchValue("");

    fetchData();
  }, [fetchData]);

  // Handle search input
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    searchRef.current = "";
    setFilteredData(allData);
    setTotalRows(allData.length);
    setCurrentPage(1);
    pageRef.current = 1;

    toast.info("Pencarian dibersihkan", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [allData]);

  // Handle page change (client-side pagination)
  const handlePageChange = useCallback((page) => {
    pageRef.current = page;
    setCurrentPage(page);
  }, []);

  // Handle per rows change
  const handlePerRowsChange = useCallback((newPerPage, page) => {
    countRef.current = parseInt(newPerPage === -1 ? 999999999 : newPerPage);
    pageRef.current = 1;
    setCurrentPage(1);
    setCount(newPerPage);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchData();
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [fetchData]);

  // Export to Excel
  const exportToExcel = useCallback(() => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("Tidak ada data untuk diexport");
      return;
    }

    try {
      const exportData = filteredData.map((item) => ({
        "KAS BANK ID": item.CashJournalID || "",
        Kepada: item.DariKepada || "",
        "Jenis Transaksi": item.JenisTransaksi || "",
        Tanggal: item.Tanggal
          ? new Date(item.Tanggal).toLocaleDateString("id-ID")
          : "",
        Keterangan: item.Keterangan || "",
        Jurnal: item.JenisCashJournal || "",
        Status: item.Status || "",
      }));

      const headers = Object.keys(exportData[0]);

      let html = `<table border="1"><tr>`;
      headers.forEach((header) => {
        html += `<th>${header}</th>`;
      });
      html += `</tr>`;

      exportData.forEach((row) => {
        html += `<tr>`;
        headers.forEach((header) => {
          const cell = row[header] || "";
          html += `<td>${cell}</td>`;
        });
        html += `</tr>`;
      });

      html += `</table>`;

      const blob = new Blob([html], {
        type: "application/vnd.ms-excel",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const filename = searchValue
        ? `Monitoring_Penerimaan_Dana_Search_${searchValue}_${new Date()
            .toISOString()
            .split("T")[0]}.xls`
        : `Monitoring_Penerimaan_Dana_${new Date().toISOString().split("T")[0]}.xls`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Data berhasil diexport ke Excel (${filteredData.length} records)`
      );
    } catch (error) {
      toast.error("Gagal mengexport data");
      console.error("Export error:", error);
    }
  }, [filteredData, searchValue]);

  // Format tanggal helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  }, []);

  // Paginasi ditangani react-data-table-component sendiri (client-side): tabel
  // WAJIB menerima seluruh filteredData, lalu RDT yang memotong per halaman
  // (Dt = kt.slice((page-1)*perPage, page*perPage) saat paginationServer=false).
  // Sebelumnya data yang dikirim sudah dipotong duluan → RDT melihat cuma 1
  // halaman sehingga tombol "berikutnya" mati. Pola ini mengikuti tabel Daftar
  // Hutang (client-side) di halaman Pembayaran AP. currentPage/count tetap
  // disinkronkan lewat onChangePage/onChangeRowsPerPage untuk penomoran kolom "No".

  // Fungsi untuk melihat detail Penerimaan Dana - MENGIKUTI FORMAT DYNAMICTABLEV2
  const handleViewDetail = useCallback((cashJournalId, e) => {
    if (e) e.stopPropagation();
    if (!cashJournalId) return;
    const formattedId = cashJournalId.toString().replace(/\//g, "-");
    navigate(`/finance/detail/form-kas-bank/${formattedId}`);
  }, [navigate]);

  // Fungsi untuk melihat blanko jika status Approved
  const handleViewBlanko = useCallback((cashJournalId, e) => {
    if (e) e.stopPropagation();
    if (!cashJournalId) return;
    const formattedId = cashJournalId.toString().replace(/\//g, "-");
    navigate(`/finance/report/blanco/pembayaran-non-po/${formattedId}`);
  }, [navigate]);

  // Memoized columns - MENGIKUTI FORMAT DYNAMICTABLEV2
  const columns = useMemo(
    () => [
      {
        name: "No",
        selector: (row, index) => (currentPage - 1) * count + index + 1,
        sortable: false,
        width: "70px",
        center: true,
      },
      {
        name: "KAS BANK ID",
        selector: (row) => row.CashJournalID,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs font-mono whitespace-normal break-all">
            {row.CashJournalID || "-"}
          </div>
        ),
      },
      {
        name: "Kepada",
        selector: (row) => row.DariKepada,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.DariKepada || "-"}
          </div>
        ),
      },
      {
        name: "Jenis Transaksi",
        selector: (row) => row.JenisTransaksi,
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.JenisTransaksi || "-"}
          </div>
        ),
      },
      {
        name: "Tanggal",
        selector: (row) => row.Tanggal,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <div className="text-xs whitespace-nowrap">
            {formatDate(row.Tanggal)}
          </div>
        ),
      },
      {
        name: "Keterangan",
        selector: (row) => row.Keterangan,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.Keterangan || "-"}
          </div>
        ),
      },
      {
        name: "Jurnal",
        selector: (row) => row.JenisCashJournal,
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.JenisCashJournal || "-"}
          </div>
        ),
      },
      {
        name: "Status",
        selector: (row) => row.Status,
        sortable: true,
        // Dilebarkan dari 120px: label "Terbayar dd/mm/yyyy" tak muat di lebar lama.
        width: "165px",
        cell: (row) => {
          const status = row.Status || "";
          const isApproved = status.toLowerCase() === "approved";
          const isRejected = status.toLowerCase() === "rejected";
          const isPending = status.toLowerCase().includes("pending") || !status;

          // "Terbayar" sengaja BUKAN angka status tersendiri, melainkan turunan dari
          // TanggalBayar — sejak jurnal terbit saat pembayaran, kolom itulah penanda
          // uang sudah benar-benar bergerak.
          if (isApproved && row.TanggalBayar) {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-700 border border-blue-200">
                Diterima {formatDate(row.TanggalBayar)}
              </span>
            );
          }

          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isApproved
                ? "bg-green-100 text-green-800 border border-green-200"
                : isRejected
                ? "bg-red-100 text-red-800 border border-red-200"
                : "bg-yellow-100 text-yellow-800 border border-yellow-200"
            }`}>
              {status || "Pending"}
            </span>
          );
        },
      },
      {
        name: "Detail",
        cell: (row) => {
          const cashJournalId = row.CashJournalID || "";
          const isApproved = row.Status && row.Status.toLowerCase() === "approved";
          
          return (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={(e) => handleViewDetail(cashJournalId, e)}
                className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                title="Lihat Detail"
              >
                Lihat
              </button>
              
              {isApproved && (
                <button
                  onClick={(e) => handleViewBlanko(cashJournalId, e)}
                  className="border text-white bg-green-600 hover:bg-white hover:border-green-600 hover:text-green-600 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                  title="Cetak Blanko"
                >
                  Blanko
                </button>
              )}

              {/* Bayar hanya untuk yang sudah disetujui tapi belum dibayar.
                  Di sinilah jurnalnya terbit. */}
              {isApproved && !row.TanggalBayar && (
                <button
                  onClick={(e) => {
                    if (e) e.stopPropagation();
                    setBayarModal({
                      show: true,
                      id: cashJournalId,
                      nominal: row.Nominal,
                      tanggalDokumen: row.Tanggal,
                    });
                  }}
                  className="border text-white bg-blue-600 hover:bg-white hover:border-blue-600 hover:text-blue-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                  title="Catat penerimaan — jurnal terbit pada tanggal yang dipilih"
                >
                  Terima
                </button>
              )}
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        // Dilebarkan dari 140px untuk menampung tombol Bayar di samping Lihat & Blanko.
        width: "210px",
      },
    ],
    [currentPage, count, formatDate, handleViewDetail, handleViewBlanko]
  );

  // Subheader component (search dan action buttons)
  const subHeaderComponentMemo = useMemo(
    () => (
      <div className="w-full mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative w-full md:w-80">
              <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Cari KAS BANK ID, Kepada, Keterangan, Status..."
                value={searchValue}
                onChange={handleSearchChange}
                disabled={!hasData || isLoading}
                className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {searchValue && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center px-4 py-2.5 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 active:scale-95 shadow-sm"
              title="Refresh Data"
            >
              <BiRefresh className={`mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              disabled={isLoading || !hasData || filteredData.length === 0}
              className="flex items-center bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Download Excel"
            >
              <BsFileEarmarkExcelFill className="mr-2" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Info pencarian */}
        {searchValue && (
          <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center text-sm">
              <BiFilter className="text-blue-500 mr-2" />
              <span className="text-blue-700 font-medium">
                Pencarian: "{searchValue}"
              </span>
              <span className="mx-2 text-gray-400">•</span>
              <span className="text-blue-600">
                Menemukan {filteredData.length} dari {allData.length} data
              </span>
              {filteredData.length > 0 && filteredData.length !== allData.length && (
                <>
                  <span className="mx-2 text-gray-400">•</span>
                  <button
                    onClick={handleClearSearch}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    Clear filter
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    ),
    [
      searchValue,
      handleSearchChange,
      handleClearSearch,
      handleRefresh,
      isLoading,
      exportToExcel,
      hasData,
      filteredData,
      allData,
    ]
  );

  // Custom styles untuk DataTable - MENGIKUTI DENGAN DYNAMICTABLEV2
  const customStyles = useMemo(
    () => ({
      head: {
        style: {
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
          color: "#374151",
          textTransform: "uppercase",
        },
      },
      headCells: {
        style: {
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "nowrap",
          borderBottom: "2px solid #e5e7eb",
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
        },
      },
      cells: {
        style: {
          fontSize: "12px",
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          minHeight: "50px",
          verticalAlign: "top",
          borderBottom: "1px solid #f3f4f6",
        },
      },
      rows: {
        style: {
          minHeight: "50px",
          "&:nth-of-type(even)": {
            backgroundColor: "#f9fafb",
          },
          "&:hover": {
            backgroundColor: "#f3f4f6",
            transition: "background-color 0.2s ease",
          },
        },
      },
      progress: {
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        },
      },
      pagination: {
        style: {
          fontSize: "12px",
          borderTop: "1px solid #e5e7eb",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    }),
    []
  );

  // Loading component
  const loadingComponent = useMemo(
    () => (
      <div className="flex flex-col justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <span className="text-gray-600">Memuat data Penerimaan Dana {bisnisUnitLabel}...</span>
        <span className="text-gray-400 text-sm mt-2">
          Mohon tunggu sebentar
        </span>
      </div>
    ),
    [bisnisUnitLabel]
  );

  // No data component - MENGIKUTI DENGAN DYNAMICTABLEV2
  const noDataComponent = useMemo(() => {
    if (!hasData && isInitialLoad) {
      return (
        <div className="w-full flex flex-col justify-center items-center py-16">
          <BsFileEarmarkExcelFill className="text-5xl text-gray-300 mb-4" />
          <div className="pb-2 text-gray-700 text-lg font-medium">
            Monitoring Penerimaan Dana {bisnisUnitLabel}
          </div>
          <div className="text-gray-500 text-sm text-center mb-6 max-w-md">
            Pilih bisnis unit dan bulan, lalu klik "Buka Data" untuk menampilkan data Penerimaan Dana
          </div>
          <div className="text-xs text-gray-400">
            Data akan ditampilkan berdasarkan periode dan bisnis unit yang dipilih ({values.BisnisUnitID})
          </div>
        </div>
      );
    }

    if (!hasData && !isInitialLoad) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">
            Tidak ada data yang ditemukan
          </div>
        </div>
      );
    }

    if (hasData && searchValue && filteredData.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">
            Tidak ada data yang cocok dengan pencarian "{searchValue}"
          </div>
          <button
            onClick={handleClearSearch}
            className="text-main-100 hover:text-main-200 font-medium"
          >
            Tampilkan semua data
          </button>
        </div>
      );
    }

    return null;
  }, [
    hasData,
    isInitialLoad,
    searchValue,
    filteredData.length,
    handleClearSearch,
    bisnisUnitLabel,
    values.BisnisUnitID,
  ]);

  return (
    <>
      <ModalBayar
        show={bayarModal.show}
        dokumenId={bayarModal.id}
        nominal={bayarModal.nominal}
        tanggalDokumen={bayarModal.tanggalDokumen}
        busy={bayarBusy}
        onSimpan={handleSimpanBayar}
        onBatal={() => setBayarModal({ show: false, id: "", nominal: "" })}
      />
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card className="shadow-lg">
                {/* Filter Section */}
                <div className="flex flex-row flex-wrap items-start justify-between w-full px-8 py-8 border-b bg-gray-50 rounded-t-lg">
                  <div className="flex flex-col md:flex-row gap-6 w-full md:w-2/3">
                    <div className="w-full md:w-1/2 lg:w-2/5 mb-4 md:mb-0">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-5 bg-blue-500 rounded mr-3"></div>
                        <label className="block uppercase tracking-wide text-gray-800 text-sm font-bold">
                          Periode Bulan
                        </label>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                        <HrdInputGenerator
                          type={"bulan"}
                          name={"Bulan"}
                          title={""}
                          value={values}
                          required={true}
                          onChange={customHandler}
                          touched={touched}
                          errors={errors}
                          size={"full"}
                        />
                      </div>
                      {values.Bulan && (
                        <div className="mt-3 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-full inline-block">
                          <span className="text-blue-700 text-xs font-medium">
                            Periode:{" "}
                            {new Date(values.Bulan).toLocaleDateString(
                              "id-ID",
                              {
                                month: "long",
                                year: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="w-full md:w-1/2 lg:w-2/5 mb-4 md:mb-0">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-5 bg-green-500 rounded mr-3"></div>
                        <label className="block uppercase tracking-wide text-gray-800 text-sm font-bold">
                          Bisnis Unit
                        </label>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                        <HrdInputGenerator
                          type={"BisnisUnit"}
                          name={"BisnisUnitID"}
                          title={""}
                          value={values}
                          required={true}
                          onChange={customHandler}
                          touched={touched}
                          errors={errors}
                          size={"full"}
                        />
                      </div>
                      {values.BisnisUnitID && (
                        <div className="mt-3 px-3 py-1.5 bg-green-50 border border-green-100 rounded-full inline-block">
                          <span className="text-green-700 text-xs font-medium">
                            Bisnis Unit: {bisnisUnitLabel} ({values.BisnisUnitID})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-full md:w-1/3 mt-6 md:mt-0">
                    <div className="flex flex-col items-start md:items-end space-y-4">
                      <div className="w-full md:w-auto">
                        <div className="flex items-center mb-2">
                          <div className="w-3 h-5 bg-blue-500 rounded mr-3"></div>
                          <label className="block uppercase tracking-wide text-gray-800 text-sm font-bold">
                            Aksi Data {bisnisUnitLabel}
                          </label>
                        </div>

                        <div className="flex flex-col space-y-4">
                          <button
                            type="button"
                            onClick={handleFilter}
                            disabled={isLoading}
                            className="inline-flex items-center justify-center text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-6 py-3 text-center transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg w-full md:w-64"
                          >
                            {isLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                                <span>Memuat Data...</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-5 h-5 mr-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                  ></path>
                                </svg>
                                <span className="font-semibold">
                                  🔍 Buka Data {bisnisUnitLabel}
                                </span>
                              </>
                            )}
                          </button>

                          {hasData && (
                            <button
                              type="button"
                              onClick={handleRefresh}
                              disabled={isLoading}
                              className="inline-flex items-center justify-center text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-200 font-medium rounded-lg text-sm px-6 py-2.5 text-center transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow w-full md:w-64"
                            >
                              <svg
                                className="w-4 h-4 mr-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                ></path>
                              </svg>
                              <span>Refresh Data</span>
                            </button>
                          )}
                        </div>

                        <div className="mt-4 text-xs text-gray-600 max-w-xs">
                          <div className="flex items-start">
                            <svg
                              className="w-4 h-4 text-blue-500 mr-2 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              ></path>
                            </svg>
                            <span>
                              Pilih bisnis unit dan bulan, lalu klik "Buka Data" untuk menampilkan
                              data Penerimaan Dana
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DataTable Section */}
                <div className="p-6">
                  {hasData && subHeaderComponentMemo}

                  <div className="overflow-x-auto border rounded-lg shadow-sm">
                    <DataTable
                      columns={columns}
                      data={filteredData}
                      customStyles={customStyles}
                      progressPending={isLoading}
                      progressComponent={loadingComponent}
                      pagination
                      paginationServer={false}
                      paginationTotalRows={filteredData.length}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      paginationPerPage={count}
                      paginationDefaultPage={currentPage}
                      highlightOnHover
                      striped
                      dense
                      noDataComponent={noDataComponent}
                      persistTableHead
                      fixedHeader
                      fixedHeaderScrollHeight="calc(100vh - 400px)"
                      responsive
                      subHeader={hasData}
                      subHeaderWrap
                    />
                  </div>

                  {/* Info tambahan - MIRIP DENGAN DYNAMICTABLEV2 */}
                  {hasData && filteredData.length > 0 && (
                    <div className="mt-4 text-sm text-gray-600">
                      {searchValue ? (
                        <div className="flex items-center justify-between">
                          <div>
                            Menampilkan {filteredData.length} dari {allData.length} data
                          </div>
                          <button
                            onClick={handleClearSearch}
                            className="text-main-100 hover:text-main-200 font-medium text-sm"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <div className="flex items-center space-x-4">
                            <div>
                              Total Data:{" "}
                              <span className="font-bold text-gray-700">
                                {allData.length}
                              </span>{" "}
                              records
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {values.Bulan && (
                              <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs">
                                Periode:{" "}
                                {new Date(values.Bulan).toLocaleDateString(
                                  "id-ID",
                                  { month: "long", year: "numeric" }
                                )}
                              </span>
                            )}
                            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                              {bisnisUnitLabel} ({values.BisnisUnitID})
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonitoringPenerimaanDana;